import { createLogger } from '@/lib/logger'

import { clearMobileStateRetry } from './auth/mobile-state-handoff'

const logger = createLogger('utils/mobile-auth')

/**
 * Mobile Authentication Utilities
 * Handles mobile app authentication flow via web login
 */

const MOBILE_PLATFORM_KEY = 'mobile_auth_platform'
const MOBILE_REDIRECT_KEY = 'mobile_auth_redirect'
const MOBILE_OAUTH_STATE_KEY = 'mobile_auth_oauth_state_v1'
const ALLOWED_MOBILE_SCHEMES = new Set([
  'scrambl',
  'scrambl-dev',
  'scrambl-local',
])
const DEFAULT_MOBILE_REDIRECT_URI =
  (import.meta.env.VITE_MOBILE_REDIRECT_URI as string | undefined) ||
  'scrambl://auth/callback'

export interface MobileAuthContext {
  platform: 'mobile'
  redirect: string
}

function isAllowedMobileRedirect(redirect: string): boolean {
  try {
    const parsed = new URL(redirect)
    const scheme = parsed.protocol.replace(':', '').toLowerCase()
    const host = parsed.hostname.toLowerCase()
    const path = parsed.pathname

    if (
      parsed.username ||
      parsed.password ||
      parsed.port ||
      parsed.hash ||
      parsed.searchParams.getAll('state').length > 1 ||
      ['accessToken', 'refreshToken', 'idToken'].some(key =>
        parsed.searchParams.has(key),
      )
    )
      return false
    if (!ALLOWED_MOBILE_SCHEMES.has(scheme)) {
      return false
    }

    // Accept:
    // - scrambl://auth/callback  -> host=auth, path=/callback
    // - scrambl:///auth/callback -> host="", path=/auth/callback
    const isExpectedCallbackPath =
      (host === 'auth' && path === '/callback') ||
      (host === '' && path === '/auth/callback')

    return isExpectedCallbackPath
  } catch {
    return false
  }
}

/**
 * Check if current request is from mobile app
 */
export function isMobileAuthFlow(): boolean {
  return sessionStorage.getItem(MOBILE_PLATFORM_KEY) === 'mobile'
}

/** Preserve a validated native entry before the login component stores its context. */
export function isMobileAuthEntry(search: string): boolean {
  const params = new URLSearchParams(search)
  if (params.get('platform') === 'mobile')
    return isAllowedMobileRedirect(params.get('redirect') ?? '')
  return isMobileAuthFlow()
}

/**
 * Get stored mobile auth context
 */
export function getMobileAuthContext(): MobileAuthContext | null {
  const platform = sessionStorage.getItem(MOBILE_PLATFORM_KEY)
  const redirect = sessionStorage.getItem(MOBILE_REDIRECT_KEY)

  if (platform === 'mobile' && redirect) {
    return { platform: 'mobile', redirect }
  }

  return null
}

/** Auth navigation keeps the active hosted request explicit on the next entry. */
export function withMobileAuthContext(path: string): string {
  const context = getMobileAuthContext()
  if (!context || !isAllowedMobileRedirect(context.redirect)) return path
  const destination = new URL(path, window.location.origin)
  if (destination.origin !== window.location.origin) return path
  destination.searchParams.set('platform', 'mobile')
  destination.searchParams.set('redirect', context.redirect)
  return destination.pathname + destination.search + destination.hash
}

/**
 * Store mobile auth context from URL params
 */
export function storeMobileAuthContext(
  platform: string | null,
  redirect: string | null,
): boolean {
  if (platform === 'mobile' && redirect) {
    clearMobileAuthContext()
    if (!isAllowedMobileRedirect(redirect)) {
      logger.warn('[MobileAuth] Ignoring untrusted mobile redirect URI')
      clearMobileAuthContext()
      return false
    }

    sessionStorage.setItem(MOBILE_PLATFORM_KEY, 'mobile')
    sessionStorage.setItem(MOBILE_REDIRECT_KEY, redirect)
    return true
  }
  clearMobileAuthContext()
  return false
}

/**
 * Clear mobile auth context
 */
export function clearMobileAuthContext(): void {
  sessionStorage.removeItem(MOBILE_PLATFORM_KEY)
  sessionStorage.removeItem(MOBILE_REDIRECT_KEY)
  sessionStorage.removeItem(MOBILE_OAUTH_STATE_KEY)
  clearMobileStateRetry()
}

/** Associate a hosted browser's native return URI with the OAuth request it made. */
export function rememberMobileOAuthRequest(authUrl: string): void {
  if (!getMobileAuthContext()) return
  const state = new URL(authUrl).searchParams.get('state')
  if (!state) throw new Error('Incomplete mobile sign-in request.')
  sessionStorage.setItem(MOBILE_OAUTH_STATE_KEY, state)
}

/** Direct native OAuth must not inherit a canceled hosted browser's return state. */
export function selectMobileOAuthContext(state: string | null): void {
  if (state && sessionStorage.getItem(MOBILE_OAUTH_STATE_KEY) === state) return
  sessionStorage.removeItem(MOBILE_PLATFORM_KEY)
  sessionStorage.removeItem(MOBILE_REDIRECT_KEY)
  sessionStorage.removeItem(MOBILE_OAUTH_STATE_KEY)
  // The linking retry map has its own exact state association.
}

/**
 * Build mobile redirect URL with tokens
 */
export function buildMobileRedirectUrl(tokens: {
  accessToken: string
  refreshToken?: string
  expiresIn?: number
  state?: string
}): string | null {
  const context = getMobileAuthContext()
  const baseUrl = context?.redirect || DEFAULT_MOBILE_REDIRECT_URI

  try {
    // Query delivery supports native auth sessions that can drop fragments.
    if (!context) {
      logger.warn(
        '[MobileAuth] Missing mobile auth context; using default callback route',
      )
    }

    if (!isAllowedMobileRedirect(baseUrl)) {
      logger.warn('[MobileAuth] Refusing to build redirect for untrusted URI')
      clearMobileAuthContext()
      return null
    }

    const redirect = new URL(baseUrl)
    // Hosted native flows put their own one-time state in the return URI. Direct
    // federated flows return the opaque signed state that the native app stored.
    if (!redirect.searchParams.has('state') && tokens.state)
      redirect.searchParams.set('state', tokens.state)
    redirect.searchParams.set('accessToken', tokens.accessToken)
    if (tokens.refreshToken)
      redirect.searchParams.set('refreshToken', tokens.refreshToken)
    if (tokens.expiresIn)
      redirect.searchParams.set('expiresIn', String(tokens.expiresIn))
    const finalUrl = redirect.toString()

    // Clear context after building URL
    clearMobileAuthContext()
    return finalUrl
  } catch {
    logger.error('[MobileAuth] Failed to build mobile redirect')
    clearMobileAuthContext()
    return null
  }
}

/**
 * Redirect to mobile app with tokens
 * Returns true if redirect was performed, false otherwise
 */
export function redirectToMobileApp(tokens: {
  accessToken: string
  refreshToken?: string
  expiresIn?: number
  state?: string
}): boolean {
  const redirectUrl = buildMobileRedirectUrl(tokens)

  if (redirectUrl) {
    window.location.href = redirectUrl
    return true
  }

  return false
}
